import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Guid } from '@helpers/guidHelper';
import { AuthenticationRequest } from '@models/ApiRequest';
import { ApiResponse, TenantDto } from '@models/ApiResponse';
import { AppError } from '@models/appError';
import * as Sentry from '@sentry/angular';
import { LmsApi } from '@services/_api/lms-api';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';
import { ReloadService } from '.';
import { PermissionType } from '../config/enums';
import { ApiEndpointsService } from './_api-endpoints.service';
import { ApiHttpService } from './_api-http.service';
import { ToastService } from './_toast/_toast.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public forceLogout = false;
  public logoutPermitted = false;
  public logoutGuid: string = null;
  public currentUser: Observable<LmsApi.AuthenticateResponse>;
  private currentUserSubject: BehaviorSubject<LmsApi.AuthenticateResponse>;
  private currentTenantSubject: BehaviorSubject<TenantDto>;
  private refreshTokenTimeout: any;
  private jwtUpdateSubject = new Subject();

  constructor(
    private httpService: ApiHttpService,
    private toastService: ToastService,
    private endpointsService: ApiEndpointsService,
    private router: Router,
    private tenantClient: LmsApi.TenantClient,
    private reloadService: ReloadService
  ) {
    this.currentTenantSubject = new BehaviorSubject<TenantDto>(
      JSON.parse(sessionStorage.getItem('currentTenant'))
    );
    this.currentUserSubject = new BehaviorSubject<LmsApi.AuthenticateResponse>(
      null
    );

    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): LmsApi.AuthenticateResponse {
    return this.currentUserSubject.value;
  }

  public get currentTenantValue(): TenantDto {
    return this.currentTenantSubject.value;
  }

  public get jwtToken() {
    return this.currentUserSubject.value?.jwtToken;
  }

  public setProfilePicture(base64file: string) {
    const currentUser = this.currentUserValue;
    currentUser.profileImageBase64 = base64file;
    this.updateUserSubject(currentUser);
  }

  public subscribeToNewJwtUpdates() {
    return this.jwtUpdateSubject.asObservable();
  }

  public authenticateTenant(subDomainName: string) {
    return this.httpService
      .get(this.endpointsService.tenantBySubDomain(subDomainName))
      .pipe(
        map((res: ApiResponse<TenantDto>) => {
          if (res.succeeded) {
            sessionStorage.setItem('currentTenant', JSON.stringify(res.data));
            this.updateTenantSubject(res.data);
          }
          return res;
        })
      );
  }

  public loginAsStudent(studentId: number) {
    this.tenantClient
      .logInAsStudent(
        new LmsApi.LogInAsUserRequest({
          tenantId: this.currentTenantValue.id,
          accountId: studentId,
        })
      )
      .pipe(take(1))
      .subscribe({
        next: async (res) => {
          if (res.succeeded && res.data.jwtToken) {
            this.replaceCurrentUserLoginInfo(res.data);

            await this.router.navigate([
              `${this.currentTenantValue.subDomainName}/portal`,
            ]);

            window.location.reload();
          }
        },
        error: (err) => this.httpService.handle(err),
      });
  }

  public loginAsStaff(staffId: number) {
    this.tenantClient
      .logInAsStaff(
        new LmsApi.LogInAsUserRequest({
          tenantId: this.currentTenantValue.id,
          accountId: staffId,
        })
      )
      .pipe(take(1))
      .subscribe({
        next: async (res) => {
          if (res.succeeded && res.data.jwtToken) {
            this.replaceCurrentUserLoginInfo(res.data);

            await this.router.navigate([
              `${this.currentTenantValue.subDomainName}/admin`,
            ]);

            window.location.reload();
          }
        },
        error: (err) => this.httpService.handle(err),
      });
  }

  public replaceCurrentUserLoginInfo(
    newAuthResponse: LmsApi.AuthenticateResponse
  ) {
    this.stopRefreshTokenTimer();
    this.updateUserSubject(newAuthResponse);
    this.updatelocalStorageTenant(newAuthResponse);
    this.updateTenantSubject(newAuthResponse.tenant);
    this.startRefreshTokenTimer(newAuthResponse.jwtToken, newAuthResponse.id);
  }

  public loginAsSelectedAccount(newAuthResponse: LmsApi.AuthenticateResponse) {
    this.stopRefreshTokenTimer();
    this.updateUserSubject(newAuthResponse);
    this.updatelocalStorageTenant(newAuthResponse);
    this.updateTenantSubject(newAuthResponse.tenant);
    this.startRefreshTokenTimer(newAuthResponse.jwtToken, newAuthResponse.id);
  }

  public revokeToken() {
    return this.httpService.post(this.endpointsService.revokeToken(), {
      accountId: this.currentUserSubject.value?.id ?? null,
    });
  }

  public login(authRequest: AuthenticationRequest) {
    return this.httpService
      .post(this.endpointsService.authenticateUser(), authRequest)
      .pipe(
        map((res: ApiResponse<LmsApi.AuthenticateResponse>) => {
          if (res.succeeded && res.data.jwtToken) {
            this.updateUserSubject(res.data);
            sessionStorage.setItem(
              'currentTenant',
              JSON.stringify(res.data.tenant)
            );
            this.updateTenantSubject(res.data.tenant);
            this.startRefreshTokenTimer(res.data.jwtToken, res.data.id);
          }
          return res;
        })
      );
  }

  public logout(
    revokeToken = true,
    explicitSubDomainName: string = null,
    forceReloadAfterLogout = false
  ) {
    this.logoutPermitted = true;
    const logoutGuid = Guid.newGuid();
    this.logoutGuid = logoutGuid;
    this.router.navigate(['logout'], {
      queryParams: {
        logoutGuid,
        revokeToken,
        explicitSubDomainName,
        forceReload: forceReloadAfterLogout,
      },
    });
  }

  public logoutStepTwo(
    revokeToken = true,
    explicitSubDomainName: string = null,
    forceReloadAfterLogout = false
  ) {
    if (!revokeToken) {
      this.finalizeLogout(explicitSubDomainName, forceReloadAfterLogout);
      return;
    }

    this.revokeToken()
      .pipe(
        take(1),
        finalize(() => {
          this.finalizeLogout(explicitSubDomainName, forceReloadAfterLogout);
        })
      )
      .subscribe();
  }

  public finalizeLogout(
    explicitSubDomainName: string = null,
    forceReloadAfterLogout = false
  ) {
    this.stopRefreshTokenTimer();
    const subDomainName =
      explicitSubDomainName ?? this.currentTenantValue.subDomainName;
    sessionStorage.removeItem('currentTenant');
    this.currentUserSubject.next(null);
    this.currentTenantSubject.next(null);
    this.logoutGuid = null;
    this.router.navigate([subDomainName]);
    if (forceReloadAfterLogout) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          // wait 5s after user logged out to force reload in order to allow ADO to start deployment and take app offline,
          // this way users will not be in a situation where they are logged into an old release and dont know to refresh to get changes
          this.reloadService.reloadPageTimeout(5000);
        }
      });
    }
    this.logoutPermitted = false;
  }

  public updateUserSubject(user: any) {
    this.currentUserSubject.next(user);
    Sentry.setUser({
      email: this.currentUserValue?.email,
      username:
        this.currentUserValue?.firstName +
          this.currentUserValue?.middleNames ===
        null
          ? ` ${this.currentUserValue?.lastName}`
          : ` ${this.currentUserValue?.middleNames} ${this.currentUserValue?.lastName}`,
    });
  }

  public updateTenantSubject(tenant: any) {
    this.currentTenantSubject.next(tenant);
  }

  public refreshToken(
    accountId?: number
  ): Observable<LmsApi.RefreshTokenResponse> {
    return this.httpService
      .post<LmsApi.RefreshTokenResponse>(this.endpointsService.refreshToken(), {
        accountId,
      })
      .pipe(take(1));
  }

  public isAuthorized(permissionType: PermissionType) {
    // const hasPermission = this.currentUserValue.permissions.find(
    //   (permission) => permission.permissionType === permissionType
    // );

    // if (hasPermission != null) {
    //   return true;
    // }
    // return false;
    return true;
  }

  // helper methods

  public startRefreshTokenTimer(authResponseToken: string, accountId: number) {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(atob(authResponseToken.split('.')[1]));

    // set a timeout to refresh the token 90 seconds before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const ninetySecondsInMs = 90000;
    const timeTillRefresh = expires.getTime() - Date.now() - ninetySecondsInMs;

    this.refreshTokenTimeout = setTimeout(
      () => this.tryRefreshToken(accountId),
      timeTillRefresh
    );
  }

  public stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  public tryRefreshToken(accountId: number): Promise<any> {
    return new Promise((resolve) => {
      this.refreshToken(accountId).subscribe(
        (res) => {
          if (res.canLogInAs.length === 1) {
            this.updateUserSubject(res.canLogInAs[0]);
            sessionStorage.setItem(
              'currentTenant',
              JSON.stringify(res.canLogInAs[0].tenant)
            );
            this.updateTenantSubject(res.canLogInAs[0].tenant);
            this.startRefreshTokenTimer(
              res.canLogInAs[0].jwtToken,
              res.canLogInAs[0].id
            );
            this.jwtUpdateSubject.next();
            resolve(true);
          }
        },
        (error: AppError) => {
          // If the reason for the refresh token failing was from too many requests,
          // we know there must be an issue with the client date time setting
          // Showing the below custom message for 2 minutes to try get the client
          // to solve the issue themselves
          if (error.status === 429) {
            this.toastService.showError(
              `${error.status}\n\n${error.message}\n\nURL: ${error.url}` +
                '\n\nThis error can be caused by having incorrect time settings on your device...' +
                '\n\nPlease make sure your device time is being set automatically. ' +
                '(Settings -> Date & Time Settings -> Set Time Automatically)',
              120000
            );
          }
          // Log user out as we are unable to refresh token automatically,
          // and thus their jwt will be expired, and will have to log in again
          this.logout();
          resolve(false);
        }
      );
    });
  }

  private updatelocalStorageTenant(authResponse: LmsApi.AuthenticateResponse) {
    sessionStorage.setItem(
      'currentTenant',
      JSON.stringify(authResponse.tenant)
    );
  }
}
